import { redirect } from "@tanstack/react-router";
import { AuthOrganisation } from "@/lib/Auth/authApi";
import { useAuthStore } from "@/lib/Auth/authStore";
import { organisationsQueryOptions } from "@/lib/Auth/organisationsQuery";
import { QueryClient } from "@/lib/ReactQuery/QueryClient";
import { clearLegacyGlobalSupplier } from "@/app/Suppliers/Utils/globalSupplier";

type ScopeLocation = { pathname: string; searchStr: string };

/**
 * Shared scope guard for org-scoped routes (the `/$tenant/$orgId/...` tree and
 * the bare `/$tenant/$orgId` supplier picker). Enforces auth, validates the
 * URL's org against the user's memberships, and keeps non-admins off the
 * `/admin` tenant.
 *
 * Returns the active organisation, or `null` when the membership list couldn't
 * be loaded (a cold cache that failed to fetch) — callers should skip their own
 * org-dependent work in that case; the GraphQL fetcher still rejects an
 * inaccessible org. Throws a router redirect for the sign-in / wrong-org /
 * wrong-tenant cases.
 */
export async function resolveOrgScope(
  tenant: string,
  orgId: string,
  location: ScopeLocation,
): Promise<AuthOrganisation | null> {
  const token = useAuthStore.getState().token;

  if (!token) {
    throw redirect({ to: "/sign-in" });
  }

  // We're on a scoped URL now (the source of truth) — drop the obsolete
  // localStorage supplier left over from the old structure.
  clearLegacyGlobalSupplier();

  // Resolve org membership against real data: cached when warm, fetched on a
  // cold cache (fresh login / hard reload / org switch). The backend + GraphQL
  // fetcher still catch an inaccessible org.
  const orgData = await QueryClient.ensureQueryData(
    organisationsQueryOptions(token),
  );

  if (!orgData) {
    return null;
  }

  const organisations = orgData.organisations ?? [];
  const activeOrganisation = organisations.find((org) => org.id === orgId);

  if (!activeOrganisation) {
    throw redirect({
      to: organisations.length === 0 ? "/onboarding" : "/org",
    });
  }

  // Non-admins can't use the `/admin` tenant; send them to `/org`. Admins may
  // sit on either prefix — `/org` is the "view as tenant" mode toggled from the
  // sidebar. Rewrites only the first path segment, preserving orgId/supplier/rest.
  if (!activeOrganisation.is_admin && tenant === "admin") {
    throw redirect({
      href: location.pathname.replace(/^\/[^/]+/, "/org") + location.searchStr,
    });
  }

  return activeOrganisation;
}
